<template>
  <div class="block-card" :class="{ 'block-disabled': disabled }">
    <div class="block-header">
      <div class="left">
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        {{ blockLabel }}
      </div>
      <span v-if="disabled" class="selected-tag">Overlaps another selection</span>
      <span v-else-if="pick === 'skip'" class="selected-tag">Skipped</span>
      <span v-else-if="pick" class="selected-tag">✓ Selected</span>
    </div>

    <label
      v-for="s in group.items"
      :key="s.id"
      class="session-row"
      :class="{ cancelled: soldOut(s) }"
    >
      <input
        type="radio"
        :name="'block-' + group.group_id"
        :value="s.id"
        :checked="pick === s.id"
        :disabled="disabled || soldOut(s)"
        @change="$emit('pick', { groupId: group.group_id, sessionId: s.id })"
      />
      <div class="session-main">
        <div class="session-title">{{ s.name }}</div>
        <div class="session-meta">
          <span v-if="sessionMeta(s)">{{ sessionMeta(s) }}</span>
          <span v-if="showCapacity(s)" class="session-spot" :class="spotClass(s)">{{ spotLabel(s) }}</span>
        </div>
        <div v-if="s.ceApprovals && s.ceApprovals.length" class="ce-chips">
          <span class="ce-chip" v-for="body in s.ceApprovals" :key="body.abbr" :title="body.name">{{ body.abbr }}</span>
        </div>
        <template v-if="s.description">
          <div class="session-desc">{{ s.description }}</div>
          <button type="button" class="session-readmore" @click.prevent.stop="$emit('open-session', s)">Read more →</button>
        </template>
      </div>
    </label>

    <label class="skip-row">
      <input
        type="radio"
        :name="'block-' + group.group_id"
        value="skip"
        :checked="pick === 'skip'"
        :disabled="disabled"
        @change="$emit('pick', { groupId: group.group_id, sessionId: 'skip' })"
      />
      <span>Skip this block (free time)</span>
    </label>
  </div>
</template>

<script>
import { sessionMeta } from '../../../ui/session'

export default {
  name: 'lp-session-card',
  props: {
    // One agenda group from GET /registration/sessions (days[].sessions[]).
    group: { type: Object, required: true },
    // Selected value for this group: a session id, 'skip', or '' (undecided).
    pick: { type: [Number, String], default: '' },
    // Blocked because a concurrent group already has a real session picked.
    disabled: { type: Boolean, default: false },
  },
  computed: {
    blockLabel() {
      return this.group.name
        ? `${this.group.name} · ${this.group.start_at} – ${this.group.end_at}`
        : `${this.group.start_at} – ${this.group.end_at}`
    },
  },
  methods: {
    sessionMeta,
    // Capacity is meaningful only when the session is tied to a room.
    showCapacity(s) {
      return s.count_capacity > 0
    },
    soldOut(s) {
      return s.count_capacity > 0 && s.available_capacity <= 0
    },
    spotClass(s) {
      if (s.available_capacity <= 0) return 'out'
      if (s.available_capacity <= 5) return 'low'
      return 'ok'
    },
    spotLabel(s) {
      if (s.available_capacity <= 0) return 'Sold out'
      return `${s.available_capacity} spots remaining`
    },
  },
}
</script>

<style lang="scss">
.block-card {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  margin-bottom: 14px;
  overflow: hidden;
}
.block-card.block-disabled { opacity: 0.5; }
.block-header {
  background: var(--brand);
  color: white;
  padding: 10px 18px;
  font-size: var(--fs-body);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
}
.block-header .left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.block-header .selected-tag {
  font-size: var(--fs-caption);
  background: rgba(255,255,255,0.22);
  padding: 2px 10px;
  border-radius: 10px;
  font-weight: 500;
}

.session-row {
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  cursor: pointer;
  align-items: flex-start;
  transition: background 0.1s;
}
.session-row + .session-row { border-top: 0.5px solid var(--border); }
.session-row:hover { background: var(--bg); }
.session-row:has(input:checked) {
  background: var(--brand-soft);
}
.session-row input { margin-top: 5px; flex-shrink: 0; width: 18px; height: 18px; }
.session-main { flex: 1; min-width: 0; }
.session-title {
  font-size: var(--fs-body);
  font-weight: 500;
  color: var(--brand-deep);
  line-height: 1.4;
  margin-bottom: 4px;
}
.session-meta {
  font-size: var(--fs-label);
  color: var(--text-tertiary);
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-bottom: 6px;
}
.session-spot { font-size: var(--fs-label); font-weight: 500; }
.session-spot.ok { color: var(--success); }
.session-spot.low { color: var(--warning); }
.session-spot.out { color: var(--danger); }
.ce-chips { display: inline-flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.ce-chip {
  font-size: var(--fs-micro); font-weight: 500;
  background: var(--brand-soft); color: var(--brand-deep);
  padding: 1px 6px; border-radius: 3px; letter-spacing: 0.04em;
}
.session-desc {
  font-size: var(--fs-small);
  color: var(--text-secondary);
  line-height: 1.55;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.session-readmore {
  font-size: var(--fs-label);
  color: var(--brand-deep);
  font-weight: 500;
  margin-top: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.session-readmore:hover { text-decoration: underline; }
.session-row.cancelled { opacity: 0.55; }
.session-row.cancelled input { pointer-events: none; }

.skip-row {
  padding: 12px 18px;
  border-top: 0.5px dashed var(--border);
  background: var(--surface-alt);
  font-size: var(--fs-small);
  color: var(--text-secondary);
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
}
.skip-row input { flex-shrink: 0; }
.skip-row:has(input:checked) {
  background: var(--bg);
  color: var(--text);
}
</style>
